// The queue's filters, sort and page live in the URL (module-10 S4), so a filtered queue can be
// shared, and "back" from a ticket keeps them. They map 1:1 onto QueueQuery (contracts/query.ts).
import type { ClosureKind, ReportedType, StageCode } from '@feedback/shared/constants/code-lists';
import { CODE_LISTS } from '@feedback/shared/constants/code-lists';
import { QUEUE_SORTS } from '@feedback/shared/contracts/query';
import type { QueryValue } from '@/lib/api';

export type QueueSort = (typeof QUEUE_SORTS)[number];

export const QUEUE_FLAGS = [
  'duplicates',
  'autoAccepted',
  'awaitingReporter',
  'notTriagedByAi',
  'assignedToMe',
  'responsibleIsMe',
  'unassigned',
] as const;
export type QueueFlag = (typeof QUEUE_FLAGS)[number];

export const QUEUE_FLAG_LABELS: Record<QueueFlag, string> = {
  duplicates: 'Duplicates and merged',
  autoAccepted: 'Auto-accepted',
  awaitingReporter: 'Awaiting Reporter',
  notTriagedByAi: 'Not triaged by AI',
  assignedToMe: 'Assigned to me',
  responsibleIsMe: 'I’m responsible for',
  unassigned: 'Not assigned to anyone',
};

export const CLOSURE_LABELS: Record<ClosureKind, string> = {
  confirmed: 'Confirmed by the reporter',
  auto_closed: 'Closed automatically',
  merged: 'Merged into another ticket',
  shipped: 'Shipped',
};

export const SORT_LABELS: Record<QueueSort, string> = {
  updatedAt: 'Last updated',
  createdAt: 'Date created',
  priority: 'Priority',
  reference: 'Reference',
};

export interface QueueSearch extends Partial<Record<QueueFlag, true>> {
  /** All tickets only (/queue): the apps to narrow to. */
  app?: string[];
  stage?: StageCode[];
  /** One status from any workflow version. */
  status?: string;
  type?: string[];
  /** The reporter's choice in the widget (poc CT-019). */
  reportedAs?: ReportedType[];
  priority?: string[];
  environment?: string[];
  closure?: ClosureKind[];
  outcome?: string[];
  q?: string;
  sort?: QueueSort;
  order?: 'asc' | 'desc';
  page?: number;
}

const MAX_VALUES = 50;

function strings(value: unknown): string[] | undefined {
  const raw = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  const list = raw.filter((v): v is string => typeof v === 'string' && v.trim() !== '').slice(0, MAX_VALUES);
  return list.length > 0 ? list : undefined;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : undefined;
}

function only<T extends string>(values: string[] | undefined, allowed: readonly T[]): T[] | undefined {
  const list = values?.filter((v): v is T => (allowed as readonly string[]).includes(v));
  return list && list.length > 0 ? list : undefined;
}

/**
 * validateSearch for /queue and /apps/$appId/queue: anything unknown or malformed is dropped. Every key is
 * returned, undefined when it's missing or invalid: TanStack Router builds a route's search as the raw search with
 * the validated one spread over it, so a key left out would keep its raw value (`?stage=bogus` reaching the page).
 */
export function parseQueueSearch(search: Record<string, unknown>): QueueSearch {
  const page = Number(search.page);
  // TanStack parses a typed `?q=1042` as a number.
  const text = typeof search.q === 'number' ? String(search.q) : search.q;
  const q = typeof text === 'string' && text.trim() ? text.trim() : undefined;
  return {
    app: strings(search.app),
    stage: only(strings(search.stage), CODE_LISTS.stage_code),
    status: typeof search.status === 'string' && search.status ? search.status : undefined,
    type: strings(search.type),
    reportedAs: only(strings(search.reportedAs), CODE_LISTS.reported_type),
    priority: strings(search.priority),
    environment: strings(search.environment),
    closure: only(strings(search.closure), CODE_LISTS.closure_kind),
    outcome: strings(search.outcome),
    ...Object.fromEntries(
      QUEUE_FLAGS.map((flag) => [flag, search[flag] === true || search[flag] === 'true' ? true : undefined]),
    ),
    q,
    sort: oneOf(search.sort, QUEUE_SORTS),
    order: oneOf(search.order, ['asc', 'desc'] as const),
    page: Number.isInteger(page) && page > 1 ? page : undefined,
  };
}

/** The query string for GET /api/apps/:appId/tickets and GET /api/tickets (which also takes appId). */
export function queueApiQuery(search: QueueSearch, pageSize: number): Record<string, QueryValue> {
  return {
    appId: search.app,
    stage: search.stage,
    status: search.status,
    type: search.type,
    reportedAs: search.reportedAs,
    priority: search.priority,
    environment: search.environment,
    closure: search.closure,
    outcome: search.outcome,
    ...Object.fromEntries(QUEUE_FLAGS.map((f) => [f, search[f]])),
    q: search.q,
    sort: search.sort,
    order: search.order,
    page: search.page,
    pageSize,
  };
}

/** How many filters are on (not search, sort or page). */
export function activeFilterCount(search: QueueSearch): number {
  const lists = [
    search.app,
    search.stage,
    search.type,
    search.reportedAs,
    search.priority,
    search.environment,
    search.closure,
    search.outcome,
  ];
  return (
    lists.filter((l) => l && l.length > 0).length +
    (search.status ? 1 : 0) +
    QUEUE_FLAGS.filter((f) => search[f]).length
  );
}

/** Adds or removes one value of a list filter. */
export function toggle<T extends string>(list: T[] | undefined, value: T, on: boolean): T[] | undefined {
  const next = on ? [...(list ?? []).filter((v) => v !== value), value] : (list ?? []).filter((v) => v !== value);
  return next.length > 0 ? next : undefined;
}
